import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Platform } from '../../enums/generated.enums';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { DateHelper } from '../../helpers/date.helper';
import { toBase64 } from '../../helpers/encoding.helper';
import { TextHelper } from '../../helpers/text.helper';
import { ActionableInsight, UserActionableInsight } from '../../types/actionable-insight.type';
import { ApplicationConfiguration } from '../../types/application-configuration.type';
import { BaseModel } from '../../types/generated.types';
import { License } from '../../types/license.type';
import { MasterProcessUserData } from '../../types/master-process-user-data.type';
import { MasterProcess } from '../../types/master-process.type';
import { ProcessBasicInfo } from '../../types/process-basic-Info.type';
import { Process } from '../../types/process.type';
import { ItemsStatistics } from '../../types/statistics/items-statistics.type';
import { AlertRulesApiService } from './alert-rules-api.service';
import { BaseApiService } from './base-api.service';
import { BenchmarkMetricsApiService } from './benchmark-metrics-api.service';
import { BillingGroupsApiService } from './billing-groups-api.service';
import { DomainsApiService } from './domains-api.service';
import { EmulatorMonitoringApiService } from './emulator-monitoring-api.service';
import { EmulatorTasksApiService } from './emulator-tasks-api.service';
import { IncidentsApiService } from './incidents-api.service';
import { ManagementMetricsApiService } from './management-metrics-api.service';
import { MetricPredictionsApiService } from './metric-predictions-api.service';
import { MetricsApiService } from './metrics-api.service';
import { MonitoringApiService } from './monitoring-api.service';
import { NotificationsApiService } from './notifications-api.service';
import { OrganizationSettingsApiService } from './organization-settings-api.service';
import { ProcessGroupsApiService } from './process-groups-api.service';
import { ProcessVersionsApiService } from './process-versions-api.service';
import { ReportsApiService } from './reports-api.service';
import { ResourceGroupsApiService } from './resource-groups-api.service';
import { ResourcesApiService } from './resources-api.service';
import { SchedulerApiService } from './scheduler-api.service';
import { SchedulerConditionStatesApiService } from './scheduler-condition-states-api.service';
import { SchedulerDumpApiService } from './scheduler-dump-api.service';
import { TasksApiService } from './tasks-api.service';
import { UserConfigurationApiService } from './user-configuration-api.service';
import { Versions } from '../../types/versions.type';
import { ImportApiService } from './import-api.service';
import { CalendarApiService } from './calendar-api.service';
import { UsersApiService } from './users-api.service';
import { DumpViewerApiService } from './dump-viewer-api.service';
import { SchedulerLoginUsersApiService } from './scheduler-login-users-api.service';
import { MonitoringConfigurationApiService } from './monitoring-configuration-api.service';
import { AuthApiService } from './auth-api.service';
import { ProcessesApiService } from './processes-api.service';
import { UtilizationApiService } from './utilization-api.service';
import { ApiCredentialsApiService } from './api.credentials-api.service';
import { TenantsApiService } from './tenants-api.service';
import { EmulatorUtilizationApiService as EmulatorUtilizationApiService } from './emulator-utilization-api.service';
import { RpaSystemApiService } from './rpa-system-api.service';
import { Region } from '../../types/region.type';
import { OrganizationsApiService } from './organizations-api.service';
import { AuditLogApiService } from './audit-log-api.service';
import { SchedulerEmulatorApiService } from './scheduler-emulator-api.service';
import { ExternalSystemsApiService } from './external-systems-api.service';
import { AnomaliesApiService } from './anomalies-api.service';
import { WorkTeamsApiService } from './work-teams-api.service';
import { WorkflowFormsApiService } from './workflow-forms-api.service';

@Injectable({ providedIn: 'root' })
export class ApiService extends BaseApiService {
  constructor(
    httpClient: HttpClient,
    public alertRules: AlertRulesApiService,
    public anomalies: AnomaliesApiService,
    public apiCredentials: ApiCredentialsApiService,
    public auditLog: AuditLogApiService,
    public auth: AuthApiService,
    public benchmarkMetrics: BenchmarkMetricsApiService,
    public billingGroups: BillingGroupsApiService,
    public calendar: CalendarApiService,
    public domains: DomainsApiService,
    public dumpViewer: DumpViewerApiService,
    public emulatorMonitoring: EmulatorMonitoringApiService,
    public emulatorTasks: EmulatorTasksApiService,
    public emulatorUtilization: EmulatorUtilizationApiService,
    public externalSystems: ExternalSystemsApiService,
    public importApi: ImportApiService,
    public incidents: IncidentsApiService,
    public managementMetrics: ManagementMetricsApiService,
    public metrics: MetricsApiService,
    public monitoring: MonitoringApiService,
    public monitoringConfiguration: MonitoringConfigurationApiService,
    public notifications: NotificationsApiService,
    public organizations: OrganizationsApiService,
    public organizationSettings: OrganizationSettingsApiService,
    public predictions: MetricPredictionsApiService,
    public processes: ProcessesApiService,
    public processVersions: ProcessVersionsApiService,
    public processGroups: ProcessGroupsApiService,
    public reports: ReportsApiService,
    public resourceGroups: ResourceGroupsApiService,
    public resources: ResourcesApiService,
    public rpaSystem: RpaSystemApiService,
    public scheduler: SchedulerApiService,
    public schedulerConditionStates: SchedulerConditionStatesApiService,
    public schedulerDump: SchedulerDumpApiService,
    public schedulerEmulator: SchedulerEmulatorApiService,
    public schedulerLoginUsers: SchedulerLoginUsersApiService,
    public tasks: TasksApiService,
    public tenants: TenantsApiService,
    public users: UsersApiService,
    public userConfiguration: UserConfigurationApiService,
    public utilization: UtilizationApiService,
    public workTeams: WorkTeamsApiService,
    public workflowForms: WorkflowFormsApiService,
  ) {
    super(httpClient);
  }

  async getVersion(): Promise<Versions> {
    return await this.get<Versions>('version');
  }

  async getDocumentationUrl(): Promise<string> {
    return await this.get<string>('documentation-url');
  }

  async getApplicationConfiguration(): Promise<ApplicationConfiguration> {
    const data = await this.get<ApplicationConfiguration>('app-configuration');
    ApiConversionHelper.convertDates(data, ['licenseExpiration']);
    return data;
  }

  async getTimeZones(): Promise<BaseModel[]> {
    return await this.get<BaseModel[]>('time-zones');
  }

  async getOrganizationSupportedRegions(): Promise<Region[]> {
    return await this.get<Region[]>('supported-regions');
  }

  // PROCESSES
  async getMasterProcesses(tenantId: string): Promise<ProcessBasicInfo[]> {
    const result = await this.get<ProcessBasicInfo[]>(`master-processes${this.getTenantParameter(tenantId)}`);
    result.forEach(p => ApiConversionHelper.convertProcessBasicInfo(p));
    return result;
  }

  async getMasterProcessReport(masterProcessId: string): Promise<MasterProcess> {
    const result = await this.get<MasterProcess>(`master-processes/${masterProcessId}/report`);
    ApiConversionHelper.convertMasterProcess(result);
    return Object.assign(new MasterProcess(), result);
  }

  async getMasterProcessUserData(processId: string): Promise<MasterProcessUserData> {
    const result = await this.get<MasterProcessUserData>(`master-processes/${processId}/user-data`);
    if (result) {
      ApiConversionHelper.convertMasterProcessUserData(result);
    }
    return result;
  }

  async getProcessReport(processId: string): Promise<Process> {
    const result = await this.get<Process>(`processes/${processId}/report`);
    ApiConversionHelper.convertProcess(result);
    return result;
  }

  addMasterProcess(file: File, tenantId: string, platform: Platform, monitoringProcessGroupId?: string): Observable<HttpEvent<{ processLabel: string; masterProcessId: string }>> {
    return this.httpClient.post<{ processLabel: string; masterProcessId: string }>(this.getUrl('master-processes'), file, {
      reportProgress: true,
      observe: 'events',
      headers: { FileName: TextHelper.removeDiacritics(file.name), TenantId: tenantId, Platform: platform, MonitoringProcessGroupId: monitoringProcessGroupId ?? '' },
    });
  }

  async cancelMasterProcess(masterProcessId: string): Promise<void> {
    return await this.get(`master-processes/${masterProcessId}/cancel`);
  }

  async deleteMasterProcess(masterProcessId: string): Promise<void> {
    return await this.delete(`master-processes/${masterProcessId}`);
  }

  async updateEstimationInterval(masterProcessId: string, masterProcessUserData: MasterProcessUserData): Promise<void> {
    return await this.patch(`master-processes/${masterProcessId}/estimation-interval`, {
      calculatedIntervalCasesCount: masterProcessUserData.calculatedIntervalCasesCount,
      enteredIntervalCasesCount: masterProcessUserData.enteredIntervalCasesCount,
      intervalTypeForCasesCounts: masterProcessUserData.intervalTypeForCasesCounts,
    });
  }

  async updateManualProcessingCosts(masterProcessId: string, manualProcessingCosts?: number): Promise<void> {
    return await this.patch(`master-processes/${masterProcessId}/manual-processing-costs`, {
      manualProcessingCosts,
    });
  }

  async updateActionableInsight(masterProcessId: string, actionableInsight: ActionableInsight): Promise<void> {
    return await this.patch(`master-processes/${masterProcessId}/actionable-insights/${actionableInsight.id}`, {
      states: [...actionableInsight.states],
    });
  }

  async addUserActionableInsight(masterProcessId: string, userActionableInsight: UserActionableInsight): Promise<void> {
    return await this.post(`master-processes/${masterProcessId}/user-actionable-insights`, userActionableInsight);
  }

  async updateUserActionableInsight(masterProcessId: string, userActionableInsight: UserActionableInsight): Promise<void> {
    userActionableInsight.savedItemMeanDuration = DateHelper.formatDuration(userActionableInsight.savedItemMeanDuration) as any as number;
    return await this.patch(`master-processes/${masterProcessId}/user-actionable-insights`, userActionableInsight);
  }

  async deleteUserActionableInsight(masterProcessId: string, actionableInsightId: string): Promise<void> {
    return await this.delete(`master-processes/${masterProcessId}/user-actionable-insights/${actionableInsightId}`);
  }

  async getItemsStatistics(processId: string, variantIds: string[]): Promise<ItemsStatistics> {
    const body = { variantIds };
    const result = await this.postWithResult<{ variantIds: string[] }, ItemsStatistics>(`processes/${processId}/items-statistics`, body, null, true);
    ApiConversionHelper.convertItemsStatistics(result);
    return result;
  }

  // DOWNLOAD
  downloadMissingLines(masterProcessId: string): void {
    this.download(`download/missing-lines/${masterProcessId}`);
  }

  // LICENSE
  async getLicenses(): Promise<License[]> {
    return await this.get<License[]>('licenses');
  }

  async createLicense(license: License): Promise<void> {
    return await this.post('licenses', license);
  }

  downloadLicenseKey(licenseId: string): void {
    this.download(`licenses/download/${licenseId}`);
  }

  // CUSTOMER FEEDBACK
  async sendCustomerFeedbackMessage(message: string, attachments?: string[]): Promise<void> {
    await this.postWithResult('feedback/message', { message, attachments });
  }

  uploadCustomerFile(file: File): Observable<HttpEvent<{ fileId: string }>> {
    return this.httpClient.post<{ fileId: string }>(this.getUrl('feedback/file'), file, {
      headers: { FileName: TextHelper.removeDiacritics(file.name) },
      reportProgress: true,
      observe: 'events',
    });
  }

  deleteCustomerFile(fileId: string): Promise<void> {
    const encodedFileId = toBase64(fileId);
    return this.delete(`feedback/file/${encodedFileId}`);
  }

  // WEBHOOKS
  async getUiPathEventsUrl(tenantId: string): Promise<string> {
    return await this.get<string>(`webhooks/uipath/event-url/${tenantId}`);
  }

  async getSchedulerWebhookUrl(): Promise<string> {
    return await this.get<string>('webhooks/orchestrator-url');
  }

  // LOGS
  public downloadLogs(from?: Date): void {
    this.download(`logs/${from != null ? DateHelper.formatApiDateOnly(from) : ''}`);
  }

  // SYNCHRONIZATION PACKAGES
  public downloadSynchroniztionPackage(from?: Date): void {
    this.download(`synchronization-packages/${from != null ? DateHelper.formatApiDateOnly(from) : ''}`);
  }
}
